import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createContext } from '@shared/utils/createContext';
import { JoyLogoLoaderBase } from './JoyLogoLoaderBase';
import { GenericPageLoader } from './GenericPageLoader';
import { useBootBranding } from './useJoyBranding';

type FullPageLoaderContextType = {
  stopTrackingKey: (key: string) => void;
  trackVisibilityForKey: (key: string, isLoading: boolean) => void;
  isAnimationCompletelyStopped: boolean | undefined;
  shouldAnimate: boolean;
  setIsAnimationCompletelyStopped: (value: boolean) => void;
};

const [Provider, useFullPageLoaderContext] = createContext<FullPageLoaderContextType>({
  name: 'FullPageLoader',
  strict: __BROWSER__,
  initialValue: {
    stopTrackingKey: () => {},
    trackVisibilityForKey: () => {},
    isAnimationCompletelyStopped: undefined,
    shouldAnimate: false,
    setIsAnimationCompletelyStopped: () => {},
  },
});

const _checkIfLoadingForAnyKey = (tracker: Record<string, boolean>) => Object.entries(tracker).some(([_key, isEnabledForKey]) => isEnabledForKey);

/**
 * TODO:
 * Per Madhur:
 * can we log an aggregate telemetry event that captures all the loaders that were shown on a given screen. and when each of them started and ended.
 * that will help us track down which ui pieces are the long pole on complex pages. there should be just one event logged with all the loaders
 * (not one per loader) so that the telemetry itself doesnt flood the network
 */
export const FullPageLoaderProvider: React.FC = ({ children }) => {
  const [tracker, setTracker] = useState<Record<string, boolean>>({});
  const [isAnimationCompletelyStopped, setIsAnimationCompletelyStopped] = useState(_checkIfLoadingForAnyKey(tracker));

  const shouldAnimate = useMemo(() => {
    return _checkIfLoadingForAnyKey(tracker);
  }, [tracker]);

  useEffect(() => {
    if (shouldAnimate) {
      setIsAnimationCompletelyStopped(false);
    }
  }, [shouldAnimate]);

  const stopTrackingKey: FullPageLoaderContextType['stopTrackingKey'] = useCallback(
    key => {
      setTracker(tracker => {
        delete tracker[key];
        return { ...tracker };
      });
    },
    [setTracker],
  );

  const trackVisibilityForKey: FullPageLoaderContextType['trackVisibilityForKey'] = useCallback(
    (key, isLoading) => {
      setTracker(tracker => {
        return {
          ...tracker,
          [key]: isLoading,
        };
      });
    },
    [setTracker],
  );

  const value = useMemo(() => {
    return { stopTrackingKey, trackVisibilityForKey, isAnimationCompletelyStopped, shouldAnimate, setIsAnimationCompletelyStopped };
  }, [stopTrackingKey, trackVisibilityForKey, isAnimationCompletelyStopped, shouldAnimate, setIsAnimationCompletelyStopped]);

  return <Provider value={value}>{children}</Provider>;
};

/**
 * FullPageLoaderRenderer - Renders a page loader.
 *
 * Branding comes from useBootBranding, which reads only server-provided data (the route's
 * isGuestFacing declaration + window.__BOOT_FLAGS__): the decision is synchronous, involves no
 * async or network, and never changes after first paint — the loader is either the Joy logo
 * or the generic spinner for the whole session, never one then the other.
 */
export const FullPageLoaderRenderer: React.FC = () => {
  const { shouldAnimate, setIsAnimationCompletelyStopped } = useFullPageLoaderContext();
  const { showJoyLogo } = useBootBranding();

  if (!__BROWSER__) {
    return null;
  }

  if (showJoyLogo) {
    return (
      <JoyLogoLoaderBase
        key="fullPageLoaderRenderer"
        shouldAnimate={shouldAnimate}
        onAnimateStop={() => setIsAnimationCompletelyStopped(true)}
      />
    );
  }

  return (
    <GenericPageLoader
      key="fullPageLoaderRenderer"
      shouldAnimate={shouldAnimate}
      onAnimateStop={() => setIsAnimationCompletelyStopped(true)}
    />
  );
};

type UseFullPageLoaderContextArgs = Readonly<{
  key: string;
  initialIsLoading?: boolean;
}>;

export const useFullPageLoader = (args: UseFullPageLoaderContextArgs) => {
  const { initialIsLoading = false, key } = args;
  const { stopTrackingKey, trackVisibilityForKey } = useFullPageLoaderContext();
  const isMountedRef = useRef(false);

  const setIsLoading = useCallback(
    (isLoading: boolean) => {
      trackVisibilityForKey(key, isLoading);
    },
    [trackVisibilityForKey, key],
  );

  useEffect(() => {
    if (__BROWSER__ && !isMountedRef.current) {
      isMountedRef.current = true;
      if (initialIsLoading) {
        setIsLoading(initialIsLoading);
      }
    }
  }, [initialIsLoading, setIsLoading]);

  useEffect(() => {
    return () => {
      stopTrackingKey(key);
    };
  }, [stopTrackingKey, key]);

  return setIsLoading;
};

export { useFullPageLoaderContext };
